import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowClockwise } from "@phosphor-icons/react";

const IDLE_MS = 4 * 60 * 60 * 1000; // show only if the app sat idle this long
const CHECK_EVERY_MS = 15 * 60 * 1000;

// Compare the main JS bundle referenced by the freshly-served index.html against
// the one this page actually loaded. Different hash => a new build was deployed.
async function newVersionAvailable() {
  try {
    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res.ok) return false;
    const html = await res.text();
    const served = html.match(/\/static\/js\/main\.[^"']+\.js/);
    const current = document.querySelector('script[src*="/static/js/main."]');
    if (!served || !current) return false;
    return !current.getAttribute("src").includes(served[0]);
  } catch {
    return false;
  }
}

export const UpdatePrompt = () => {
  const [show, setShow] = useState(false);
  const lastActiveRef = useRef(Date.now());
  const snoozedUntilRef = useRef(0);

  useEffect(() => {
    const markActive = () => { lastActiveRef.current = Date.now(); };
    ["pointerdown", "keydown", "touchstart"].forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

    let checking = false;
    const maybePrompt = async () => {
      if (checking || document.hidden) return;
      const idle = Date.now() - lastActiveRef.current;
      if (idle < IDLE_MS || Date.now() < snoozedUntilRef.current) return;
      checking = true;
      if (await newVersionAvailable()) setShow(true);
      checking = false;
    };

    const timer = setInterval(maybePrompt, CHECK_EVERY_MS);
    const onVisible = () => { if (!document.hidden) maybePrompt(); };
    document.addEventListener("visibilitychange", onVisible);

    if (new URLSearchParams(window.location.search).get("pwa_update_test") === "1") {
      setTimeout(() => setShow(true), 2000);
    }
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      ["pointerdown", "keydown", "touchstart"].forEach((ev) => window.removeEventListener(ev, markActive));
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md"
          data-testid="pwa-update-banner"
        >
          <div className="rounded-2xl bg-[#0A0D14] text-white shadow-2xl px-4 py-3 flex items-center gap-3 border border-white/10">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">An updated version is available</div>
              <div className="text-xs text-white/60">Reload to get the latest.</div>
            </div>
            <button
              type="button" data-testid="pwa-update-reload"
              className="shrink-0 flex items-center gap-1.5 rounded-full bg-[#CCFF00] text-[#000000] text-sm font-bold px-4 py-2 hover:brightness-95 transition-[filter]"
              onClick={() => window.location.reload()}
            >
              <ArrowClockwise size={15} weight="bold" /> Reload
            </button>
            <button
              type="button" data-testid="pwa-update-later"
              className="shrink-0 text-xs text-white/50 hover:text-white/80 transition-colors"
              onClick={() => { snoozedUntilRef.current = Date.now() + IDLE_MS; setShow(false); }}
            >
              Later
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
